// OWNER: FRONTEND-ADMIN. Browser side of the voicemail-drop recorder (SPEC §15.8):
// MediaRecorder capture → decode + telephone band-pass + mono mixdown in an OfflineAudioContext →
// 8 kHz 16-bit PCM WAV via the pure encoder in wav.ts.
import { DROP_SAMPLE_RATE, MAX_DROP_SECONDS, buildDropWav, readWavHeader } from './wav';

export class RecorderError extends Error {}

type OfflineCtor = new (channels: number, length: number, sampleRate: number) => OfflineAudioContext;
function offlineCtor(): OfflineCtor {
  const w = window as unknown as { OfflineAudioContext?: OfflineCtor; webkitOfflineAudioContext?: OfflineCtor };
  const C = w.OfflineAudioContext ?? w.webkitOfflineAudioContext;
  if (!C) throw new RecorderError('This browser can’t process audio. Use a current Chrome, Edge, Firefox or Safari.');
  return C;
}

/** Plain-English reason the recorder can't run here, or null when it can. */
export function recorderUnsupportedReason(): string | null {
  if (typeof window === 'undefined') return 'No browser.';
  if (!window.isSecureContext) return 'Recording needs a secure (https) page.';
  if (!navigator.mediaDevices?.getUserMedia) return 'This browser can’t use the microphone. Use a current Chrome, Edge, Firefox or Safari.';
  if (typeof MediaRecorder === 'undefined') return 'This browser can’t record audio (no MediaRecorder). Use a current Chrome, Edge, Firefox or Safari.';
  try { offlineCtor(); } catch (e) { return (e as Error).message; }
  return null;
}

export function pickMimeType(): string | undefined {
  const prefs = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4', 'audio/webm'];
  return prefs.find((t) => typeof MediaRecorder.isTypeSupported === 'function' && MediaRecorder.isTypeSupported(t));
}

export async function openMic(): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 } });
  } catch (e) {
    const name = (e as DOMException)?.name;
    if (name === 'NotAllowedError' || name === 'SecurityError') throw new RecorderError('Microphone access was blocked. Click the mic icon in the address bar, allow the microphone for this site, and try again.');
    if (name === 'NotFoundError' || name === 'OverconstrainedError') throw new RecorderError('No microphone was found. Plug in your headset and try again.');
    if (name === 'NotReadableError') throw new RecorderError('The microphone is busy in another app (Zoom, Teams, a call). Close it and try again.');
    throw new RecorderError(`Couldn't open the microphone: ${(e as Error)?.message ?? String(e)}`);
  }
}

export function stopStream(stream: MediaStream | null): void {
  stream?.getTracks().forEach((t) => t.stop());
}

export interface ProcessedRecording { wav: Blob; durationSec: number; sampleRate: number; bytes: number }

/** Recorded blob (webm/ogg/mp4) → 8 kHz mono 16-bit PCM WAV, trimmed and normalised. */
export async function toDropWav(recorded: Blob): Promise<ProcessedRecording> {
  if (recorded.size === 0) throw new RecorderError('Nothing was recorded. Check the microphone and try again.');
  const Ctor = offlineCtor();
  const encoded = await recorded.arrayBuffer();

  // 1) decode at 48 kHz (a throwaway 1-frame context is enough for decodeAudioData)
  let decoded: AudioBuffer;
  try {
    decoded = await new Ctor(1, 1, 48000).decodeAudioData(encoded.slice(0));
  } catch {
    throw new RecorderError('The browser recorded audio it can’t read back. Try again, or use Chrome.');
  }
  if (decoded.duration < 1) throw new RecorderError('The recording is under a second long. Record the whole script.');

  // 2) telephone band (≈120 Hz – 3.4 kHz) and mono mixdown, rendered offline at the source rate
  const rate = decoded.sampleRate;
  const ctx = new Ctor(1, decoded.length, rate);
  const src = ctx.createBufferSource();
  src.buffer = decoded;
  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass'; hp.frequency.value = 120; hp.Q.value = Math.SQRT1_2;
  const lp1 = ctx.createBiquadFilter();
  lp1.type = 'lowpass'; lp1.frequency.value = 3400; lp1.Q.value = Math.SQRT1_2;
  const lp2 = ctx.createBiquadFilter();
  lp2.type = 'lowpass'; lp2.frequency.value = 3400; lp2.Q.value = Math.SQRT1_2;
  src.connect(hp); hp.connect(lp1); lp1.connect(lp2); lp2.connect(ctx.destination); // 1-channel destination = mono mixdown
  src.start(0);
  const filtered = await ctx.startRendering();

  // 3) band-limited resample to 8 kHz, trim, normalise, encode (pure, unit-tested)
  const { bytes, durationSec } = buildDropWav([filtered.getChannelData(0)], rate);
  const info = readWavHeader(bytes);
  if (info.sampleRate !== DROP_SAMPLE_RATE || info.channels !== 1 || info.bitsPerSample !== 16) {
    throw new RecorderError('The audio conversion produced the wrong format. Tell PJ.');
  }
  if (durationSec > MAX_DROP_SECONDS + 0.05) {
    throw new RecorderError(`The recording is ${durationSec.toFixed(1)} s after trimming silence; the limit is ${MAX_DROP_SECONDS} s. Read it a little faster.`);
  }
  if (durationSec < 3) throw new RecorderError('Only a couple of seconds of speech were picked up. Move closer to the mic and read the whole script.');
  const copy = new Uint8Array(bytes.length);
  copy.set(bytes);
  return { wav: new Blob([copy.buffer], { type: 'audio/wav' }), durationSec, sampleRate: info.sampleRate, bytes: bytes.length };
}
